import { FC, ReactElement, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { HeaderNotifications } from '@/compositions'
import { useFeedConnection } from '@/context/FeedConnectionContext'
import { NotificationItem, NotificationSource } from '@/entities/Notification/Notification'
import { mergeNotificationSources } from '@/entities/Notification/NotificationTransformers'
import { trans } from '@/helpers/trans'
import { useChatNotifications } from '@/hooks/useChatNotifications'

/**
 * The single, app-wide notifications bell - mounted once (see PrivateRouteGroup), not per page,
 * so it always shows both feed and chat notifications together rather than whichever source
 * happens to be mounted. Its unread counts still feed the dots elsewhere (tabs, nav, hamburger)
 * on every page, but the bell/popover itself only renders on the feed and chat pages.
 */
export const ConnectedNotifications: FC = (): ReactElement | null => {
    const navigate = useNavigate()
    const location = useLocation()
    const feed = useFeedConnection()
    const chat = useChatNotifications()

    const notifications = useMemo(
        () => mergeNotificationSources(feed.notifications, chat.notifications),
        [feed.notifications, chat.notifications],
    )

    const markAllRead = (): void => {
        feed.markAllRead()
        chat.markAllRead()
    }

    const communicationPath = `/${trans('nav.communication.link')}`

    const handleNotificationClick = (notification: NotificationItem): void => {
        if (notification.source === NotificationSource.feed) {
            const newsfeedPath = `${communicationPath}/${trans('nav.communication.newsfeed.link')}`

            navigate(newsfeedPath, { state: { focusActivityId: notification.id } })
        } else {
            const messagesPath = `${communicationPath}/${trans('nav.communication.messages.link')}`

            navigate(`${messagesPath}/${notification.id}`)
        }
    }

    if (! location.pathname.startsWith(communicationPath)) {
        return null
    }

    return (
        <HeaderNotifications
            notifications={notifications}
            unreadCount={feed.unreadCount + chat.unreadCount}
            loading={feed.loading}
            onMarkAllRead={markAllRead}
            onNotificationClick={handleNotificationClick}
        />
    )
}
